import { ComponentProps, FC, FunctionComponent, useContext } from "react";
import styled from "styled-components";
import { ButtonProps, PrimaryButton } from "../../../components/button";
import { StepperContext } from "../../../components/stepper/context";
import {
  Widget,
  WidgetProps,
  WIDGET_BORDER_RADIUS,
} from "../../../components/widget";
import { colours } from "../../../styles/colours";
import { device } from "../../../styles/media";
import { ConfigContext } from "./config-context";

export const StyledWidget = styled(Widget)``;

export const Inner = styled.form<{ background?: string; foreground?: string }>`
  background: ${(props) => props.background || colours.white};
  color: ${(props) => props.foreground || "inherit"};
  padding: 1em;
  border-radius: ${WIDGET_BORDER_RADIUS};

  @media ${device.laptop} {
    overflow: auto;
    padding: 4em 1em;
  }

  ${StyledWidget} {
    margin-bottom: 1em;
  }
  ${StyledWidget}:last-child {
    margin-bottom: 3em;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 50em;
  margin: auto;
`;

export const Container: FC<
  Omit<ComponentProps<typeof Inner>, "foreground" | "background">
> = ({ children, className, ...props }) => {
  const { next } = useContext(StepperContext);
  const { config } = useContext(ConfigContext);

  return (
    <Inner
      onSubmit={(e) => next.next(e)}
      foreground={config.configuration.css.textColor}
      background={config.configuration.css.formBackgroundColor}
      {...props}
    >
      <Wrapper className={className}>
        {children}
        <input type="submit" hidden />
      </Wrapper>
    </Inner>
  );
};

const InnerFormPrimaryButton = styled(
  ({ background, primaryCta, secondaryCta, ...props }) => (
    <PrimaryButton {...props} />
  )
)<{
  background?: string;
  primaryCta?: string;
  secondaryCta?: string;
}>`
  ${(props) => props.primaryCta && `background: ${props.primaryCta}`};
  ${(props) => props.secondaryCta && `color: ${props.secondaryCta}`};

  &.Mui-disabled {
    ${(props) =>
      (props.primaryCta && `background: ${props.primaryCta}aa`) || ""};
    ${(props) => props.secondaryCta && `color: ${props.secondaryCta}aa`};
  }

  &:hover {
    ${(props) => props.primaryCta && `color: ${props.primaryCta}`};
    ${(props) => props.secondaryCta && `background: ${props.secondaryCta}`};
  }
`;

export const FormPrimaryButton: FunctionComponent<ButtonProps> = (props) => {
  const { config } = useContext(ConfigContext);
  return (
    <InnerFormPrimaryButton
      background={config.configuration.css.formBackgroundColor}
      primaryCta={config.configuration.css.buttonPrimaryColor}
      secondaryCta={config.configuration.css.buttonSecondaryColor}
      {...props}
    />
  );
};

const InnerFormSecondaryButton = styled(
  ({ background, primaryCta, secondaryCta, ...props }) => (
    <PrimaryButton {...props} />
  )
)<{
  background?: string;
  primaryCta?: string;
  secondaryCta?: string;
}>`
  ${(props) => props.secondaryCta && `background: ${props.secondaryCta}`};
  ${(props) => props.primaryCta && `color: ${props.primaryCta}`};

  &.Mui-disabled {
    ${(props) =>
      (props.secondaryCta && `background: ${props.secondaryCta}22`) || ""};
    ${(props) => props.primaryCta && `color: ${props.primaryCta}cc`};
  }

  &:hover {
    ${(props) => props.secondaryCta && `color: ${props.secondaryCta}`};
    ${(props) => props.primaryCta && `background: ${props.primaryCta}`};
  }
`;

export const FormSecondaryButton: FunctionComponent<{}> = (props) => {
  const { config } = useContext(ConfigContext);
  return (
    <InnerFormSecondaryButton
      background={config.configuration.css.formBackgroundColor}
      primaryCta={config.configuration.css.buttonPrimaryColor}
      secondaryCta={config.configuration.css.buttonSecondaryColor}
      {...props}
    />
  );
};

const InnerFormWidget = styled(Widget)<{
  background?: string;
  foreground?: string;
}>`
  background: ${(props) => props.background || colours.white};
  color: ${(props) => props.foreground || "inherit"};
`;

export const FormWidget: FunctionComponent<WidgetProps> = (props) => {
  const { config } = useContext(ConfigContext);
  return (
    <InnerFormWidget
      background={config.configuration.css.formBackgroundColor}
      foreground={config.configuration.css.textColor}
      {...props}
    />
  );
};
